import {
    parsePhoneNumberWithError,
    type CountryCode,
} from 'libphonenumber-js/min';

/**
 * Phone number utility functions for Quipex UI.
 *
 * IMPORTANT: Function Behavior Asymmetry
 *
 * These functions have intentionally different error handling behaviors:
 *
 * - toPhoneISO(): THROWS on invalid input (for API calls)
 * - toPhoneNational(): Returns original input with console warning (for UI display)
 *
 * This asymmetry is by design:
 * - Use toPhoneISO() when sending data to APIs - strict validation prevents invalid data
 * - Use toPhoneNational() when displaying data in UI - graceful fallback maintains user experience
 *
 * Usage examples:
 *
 * // For API calls (E.164 format - no spaces) - THROWS on invalid input
 * const apiPhone = toPhoneISO('0412 345 678'); // '+61412345678'
 * const apiPhone = toPhoneISO('invalid'); // throws Error
 *
 * // For UI display (National format - with spaces) - Returns input with warning
 * const displayPhone = toPhoneNational('0412 345 678'); // '0412 345 678'
 * const displayPhone = toPhoneNational('invalid'); // 'invalid' (with console warning)
 *
 * // Validation
 * const isValid = isValidPhone('0412 345 678'); // true
 */

/**
 * Converts phone numbers to E.164 format for API calls.
 *
 * IMPORTANT: This function THROWS on invalid input to ensure data integrity for API calls.
 * Use this function when sending phone numbers to APIs or storing in databases.
 *
 * Ensures consistent phone number format when **SENDING** data via APIs.
 * E.164 format is the international standard: +[country code][national number]
 *
 * @param phoneNumber - The phone number to convert (can be in various formats)
 * @param defaultCountry - Default country code to use when parsing numbers without country code (defaults to 'AU')
 * @returns E.164 formatted phone number string Returns the original value for null/undefined/empty strings for backwards compatibility.
 * @throws Error if the phone number cannot be parsed or is invalid
 *
 * @example
 * ```typescript
 * // Australian numbers
 * toPhoneISO('0412 345 678') // returns '+61412345678'
 * toPhoneISO('02 9876 5432') // returns '+61298765432'
 * toPhoneISO('+61 2 9876 5432') // returns '+61298765432'
 *
 * // International numbers
 * toPhoneISO('+1 212 555 0123') // returns '+12125550123'
 * toPhoneISO('212-555-0123', 'US') // returns '+12125550123'
 *
 * // Various input formats
 * toPhoneISO('(02) 9876-5432') // returns '+61298765432'
 * toPhoneISO('02.9876.5432') // returns '+61298765432'
 *
 * // Usage in API calls (similar to toLocalISO for dates)
 * const contactData = {
 *   name: 'John Doe',
 *   phone: toPhoneISO('0412 345 678'), // Standardized for API
 *   email: 'john@example.com'
 * };
 *
 * // FormData example
 * const formData = new FormData();
 * formData.append('phone', toPhoneISO(phoneNumber));
 * ```
 */
export function toPhoneISO(
    phoneNumber: string,
    defaultCountry?: CountryCode
): string;
export function toPhoneISO(
    phoneNumber: null | undefined,
    defaultCountry?: CountryCode
): null | undefined;
export function toPhoneISO(
    phoneNumber: string | null | undefined,
    defaultCountry: CountryCode = 'AU'
): string | null | undefined {
    // Pass through null/undefined values for backwards compatibility
    if (phoneNumber == null) {
        return phoneNumber;
    }

    // Pass through empty strings for backwards compatibility
    if (phoneNumber.trim() === '') {
        return phoneNumber;
    }

    try {
        const parsed = parsePhoneNumberWithError(phoneNumber, defaultCountry);

        if (!parsed.isPossible()) {
            console.error(`[toPhoneISO] Invalid phone number provided`);
            throw new Error(`Invalid phone number`);
        }

        return parsed.number; // Returns E.164 format
    } catch (error) {
        if (error instanceof Error) {
            console.error(
                `[toPhoneISO] Failed to parse phone number: ${error.message}`
            );
            throw new Error(`Failed to parse phone number: ${error.message}`);
        }
        console.error(`[toPhoneISO] Failed to parse phone number`);
        throw new Error(`Failed to parse phone number`);
    }
}

/**
 * Converts phone numbers to national format for display purposes.
 *
 * IMPORTANT: This function returns the original input with a console warning for invalid numbers.
 * Use this function for UI display to maintain user experience even with invalid data.
 *
 * Formats phone numbers in national format with proper spacing for human readability (e.g., '0412 345 678').
 * Returns original input for invalid numbers to maintain backwards compatibility.
 *
 * @param phoneNumber - The phone number to convert
 * @param defaultCountry - Default country code (defaults to 'AU')
 * @returns National formatted phone number or original input if invalid
 *
 * @example
 * ```typescript
 * toPhoneNational('0412 345 678') // '0412 345 678'
 * toPhoneNational('02 9876 5432') // '02 9876 5432'
 * toPhoneNational('invalid-phone') // 'invalid-phone' (with console warning)
 * ```
 */
export function toPhoneNational(
    phoneNumber: string,
    defaultCountry?: CountryCode
): string;
export function toPhoneNational(
    phoneNumber: null | undefined,
    defaultCountry?: CountryCode
): null | undefined;
export function toPhoneNational(
    phoneNumber: string | null | undefined,
    defaultCountry: CountryCode = 'AU'
): string | null | undefined {
    // Pass through null/undefined values for backwards compatibility
    if (phoneNumber == null) {
        return phoneNumber;
    }

    // Pass through empty strings for backwards compatibility
    if (phoneNumber.trim() === '') {
        return phoneNumber;
    }

    try {
        const parsed = parsePhoneNumberWithError(phoneNumber, defaultCountry);

        if (!parsed.isPossible()) {
            // For backwards compatibility, return original input instead of throwing
            // This handles historical data that may not have been properly validated
            console.warn(
                `[toPhoneNational] Invalid phone number - returning original input for backwards compatibility`
            );
            return phoneNumber;
        }

        return parsed.formatNational(); // Returns national format with spaces
    } catch (error) {
        // For backwards compatibility, return original input instead of throwing
        // This handles historical data that may not have been properly validated
        console.warn(
            `[toPhoneNational] Failed to parse phone number - returning original input for backwards compatibility`,
            error instanceof Error ? error.message : error
        );
        return phoneNumber;
    }
}

/**
 * Validates if a phone number is valid and can be converted to E.164 format.
 *
 * @param phoneNumber - The phone number to validate
 * @param defaultCountry - Default country code to use when parsing numbers without country code (defaults to 'AU')
 * @returns true if the phone number is valid and can be converted to E.164, false otherwise
 *
 * @example
 * ```typescript
 * isValidPhone('0412 345 678') // returns true
 * isValidPhone('not-a-number') // returns false
 * isValidPhone('') // returns false
 *
 * // Usage in form validation
 * if (isValidPhone(phoneNumber)) {
 *   const e164Phone = toPhoneISO(phoneNumber);
 *   // Send to API
 * }
 * ```
 */
export function isValidPhone(
    phoneNumber: string | null | undefined,
    defaultCountry: CountryCode = 'AU'
): boolean {
    if (!phoneNumber || phoneNumber.trim() === '') {
        return false;
    }

    try {
        const parsed = parsePhoneNumberWithError(phoneNumber, defaultCountry);
        return parsed.isPossible();
    } catch {
        return false;
    }
}
